// frontend/src/widgets/MetricGaugePanelWidget.jsx
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Leon du Plooy - Castle Traffic Ltd - Copyright 2026
// Contact:      dev@castle-traffic.co.uk
// Created:      2025-07-04
// Updated:      2026-09-08 16:06 BST
//
// Description:
//   Dashboard widget panel showing CPU, RAM, Disk, and GPU gauges.
//
// Lifecycle Status:
//   Active.
//
// Change Summary (2026-08-27):
//   - Removed automatic system-status fetch on widget mount.
//   - Removed 30-second background polling.
//   - Metrics are now retrieved only by explicit user action.
//   - Added Load Metrics / Refresh Metrics controls.
//   - Added loading, error, and last-updated states.
//   - Preserved the existing MetricGaugeWidget presentation.
//
// Mobile Responsiveness (2026-09-08):
//   - Preserves the existing MetricGaugeWidget presentation and data semantics.
//   - Allows gauges to wrap naturally into a single-column flow on narrow screens.
//   - Uses compact mobile spacing while retaining the wider desktop presentation.
//   - Makes Load Metrics / Refresh Metrics controls full-width and touch-friendly
//     on mobile while remaining compact on larger screens.
//   - Stacks the last-updated text and refresh control on narrow screens.
//   - Allows errors and timestamp text to wrap safely without horizontal overflow.
//   - Uses natural height on mobile while preserving desktop fill behaviour.
//   - Uses one render path for desktop and mobile; no duplicate mobile DOM.
//
// API / Authentication Alignment (2026-09-08):
//   - Replaced direct axios usage with the canonical axiosInstance.
//   - The expensive /system/status endpoint remains user-triggered only.
//   - No automatic request is made on mount.
//
// Operational Notes:
//   - GET /api/system/status is an expensive diagnostic endpoint and must not
//     be polled automatically.
//   - Mounting this widget must generate zero /api/system/status requests.
//   - Administrators may request a current snapshot explicitly.
//
// Future Intent:
//   - Restrict infrastructure diagnostics through the platform permission model.
//   - If continuous monitoring becomes necessary, use cached/background metrics
//     rather than synchronous per-request host inspection.

import React, {
  useCallback,
  useState,
} from "react";

import axiosInstance from "../api/axiosInstance";
import MetricGaugeWidget from "./MetricGaugeWidget";

const API_PATH = "/system/status";

const MetricGaugePanelWidget = () => {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchMetrics = useCallback(async () => {
    if (loading) {
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response =
        await axiosInstance.get(
          API_PATH
        );

      setMetrics(
        response.data
      );

      setLastUpdated(
        new Date()
      );
    } catch (requestError) {
      console.error(
        "[MetricGaugePanelWidget] Failed to fetch system status:",
        requestError
      );

      setError(
        requestError?.response?.data?.detail ||
          requestError?.message ||
          "Unable to retrieve system metrics."
      );
    } finally {
      setLoading(false);
    }
  }, [loading]);

  if (!metrics) {
    return (
      <div
        className="flex h-auto min-h-[180px] w-full flex-col items-center justify-center gap-3 p-4 text-center md:h-full md:p-5"
      >
        <div
          className="w-full max-w-[420px] break-words text-sm leading-6 text-gray-500"
          style={{
            overflowWrap:
              "anywhere",
          }}
        >
          System metrics are collected on demand and are not monitored
          automatically.
        </div>

        {error && (
          <div
            role="alert"
            className="w-full max-w-[420px] break-words text-sm text-red-700"
            style={{
              overflowWrap:
                "anywhere",
            }}
          >
            {error}
          </div>
        )}

        <button
          type="button"
          onClick={
            fetchMetrics
          }
          disabled={
            loading
          }
          className="min-h-[44px] w-full max-w-[420px] rounded-md border border-slate-300 px-4 py-2 font-semibold sm:w-auto"
          style={{
            backgroundColor:
              loading
                ? "#f1f5f9"
                : "#ffffff",
            color:
              loading
                ? "#64748b"
                : "#111827",
            cursor:
              loading
                ? "default"
                : "pointer",
          }}
        >
          {loading
            ? "Loading Metrics..."
            : "Load Metrics"}
        </button>
      </div>
    );
  }

  const {
    cpu_percent,
    memory = {},
    disk = {},
    gpu = {},
  } = metrics;

  const gpuLoad =
    gpu?.util_gpu ??
    gpu?.load ??
    0;

  return (
    <div className="flex h-auto min-h-[220px] w-full flex-col overflow-visible md:h-full md:overflow-hidden">
      <div className="flex w-full flex-col items-center justify-center gap-4 p-3 sm:flex-row sm:flex-wrap sm:gap-5 md:gap-6 md:p-4">
        <MetricGaugeWidget
          label="CPU Usage"
          value={
            Number(
              cpu_percent
            ) || 0
          }
          warning={70}
          critical={90}
        />

        <MetricGaugeWidget
          label="Memory Usage"
          value={
            Number(
              memory?.percent
            ) || 0
          }
          warning={70}
          critical={90}
        />

        <MetricGaugeWidget
          label="Disk Usage"
          value={
            Number(
              disk?.percent
            ) || 0
          }
          warning={80}
          critical={95}
        />

        {gpu?.available && (
          <MetricGaugeWidget
            label="GPU Load"
            value={
              Number.parseFloat(
                gpuLoad
              ) || 0
            }
            warning={70}
            critical={90}
          />
        )}
      </div>

      {error && (
        <div
          role="alert"
          className="w-full break-words px-3 pb-2 text-center text-sm text-red-700"
          style={{
            overflowWrap:
              "anywhere",
          }}
        >
          {error}
        </div>
      )}

      <div className="mt-auto flex w-full flex-col items-stretch gap-3 px-3 pb-3 pt-2 sm:flex-row sm:items-center sm:justify-between md:px-3">
        <div
          className="min-w-0 break-words text-center text-xs italic text-gray-500 sm:text-left"
          style={{
            overflowWrap:
              "anywhere",
          }}
        >
          Last updated:{" "}
          {lastUpdated
            ? lastUpdated.toLocaleString()
            : "Never"}
        </div>

        <button
          type="button"
          onClick={
            fetchMetrics
          }
          disabled={
            loading
          }
          className="min-h-[44px] w-full rounded-md border border-slate-300 px-3 py-2 text-sm font-semibold sm:min-h-0 sm:w-auto sm:py-[7px]"
          style={{
            backgroundColor:
              loading
                ? "#f1f5f9"
                : "#ffffff",
            color:
              loading
                ? "#64748b"
                : "#111827",
            cursor:
              loading
                ? "default"
                : "pointer",
          }}
        >
          {loading
            ? "Refreshing..."
            : "Refresh Metrics"}
        </button>
      </div>
    </div>
  );
};

export default MetricGaugePanelWidget;
